import { createContext, useContext, useSyncExternalStore } from "react"

export type DropSignal =
	| { kind: "before" | "after"; id: string }
	| { kind: "nest"; id: string }
	| null

// One store drives both the insertion line (before/after a row) and the nest
// highlight. A subscription store, not a context value, so only the affected
// rows re-render on each pointer move instead of the whole nav tree.
export function createDropSignalStore() {
	let current: DropSignal = null
	const listeners = new Set<() => void>()
	const same = (a: DropSignal, b: DropSignal) =>
		a === b || (!!a && !!b && a.kind === b.kind && a.id === b.id)
	return {
		get: () => current,
		set: (next: DropSignal) => {
			if (same(next, current)) return
			current = next
			listeners.forEach((listener) => listener())
		},
		subscribe: (listener: () => void) => {
			listeners.add(listener)
			return () => {
				listeners.delete(listener)
			}
		},
	}
}

export type DropSignalStore = ReturnType<typeof createDropSignalStore>

const DropSignalContext = createContext<DropSignalStore | null>(null)

export const DropSignalProvider = DropSignalContext.Provider

const noopSubscribe = () => () => undefined

// Returns the drop state for a given row: "before"/"after" for the insertion
// line, "nest" for the nesting highlight, or null.
export function useDropSignal(id: string): "before" | "after" | "nest" | null {
	const store = useContext(DropSignalContext)
	return useSyncExternalStore(store?.subscribe ?? noopSubscribe, () => {
		const current = store?.get()
		return current && current.id === id ? current.kind : null
	})
}
